/* ============================================
   EVENT MAPPING - Tables de correspondance
   ============================================
   FICHIER CRITIQUE - Contient tous les IDs
   ============================================ */

import { t } from '../i18n/i18n.js'

/**
 * Table de correspondance officielle des codes de statut (F6)
 * Format: ID → Clé de traduction i18n
 */
export const STAT_MAP_KEYS = {
  1: 'events.goal',
  2: 'events.corner',
  3: 'events.yellowCard',
  4: 'events.redCard',
  5: 'events.offside',
  6: 'events.freeKick',
  7: 'events.goalKick',
  8: 'events.penalty',
  9: 'events.substitution',
  10: 'events.matchStart',
  11: 'events.halfTime',
  12: 'events.matchEnd',
  13: 'events.halfTimeScore',
  15: 'events.cardConfirmed',
  16: 'events.penaltyMissed',
  17: 'events.ownGoal',
  19: 'events.addedTime',
  21: 'events.shotOnTarget',
  22: 'events.shotOffTarget',
  23: 'events.attack',
  24: 'events.dangerousAttack',
  25: 'events.possession',
  26: 'events.extraTimeEnded',
  27: 'events.penaltyEnded',
  28: 'events.var',
  29: 'events.penaltyShootout',
  30: 'events.penaltyShootoutMissed',
  37: 'events.blockedShot'
}

/**
 * Table de correspondance officielle (rétrocompatibilité)
 * Format: ID → Nom traduit de la statistique
 */
export const STAT_MAP = {
  1: () => t('events.goal', 'But'),
  2: () => t('events.corner', 'Corner'),
  3: () => t('events.yellowCard', 'Carton jaune'),
  4: () => t('events.redCard', 'Carton rouge'),
  5: () => t('events.offside', 'Hors-jeu'),
  6: () => t('events.freeKick', 'Coup franc'),
  7: () => t('events.goalKick', 'Coup de pied de but'),
  8: () => t('events.penalty', 'Penalty'),
  9: () => t('events.substitution', 'Remplacement'),
  10: () => t('events.matchStart', 'Début'),
  11: () => t('matchStates.halfTime', 'Mi-temps'),
  12: () => t('events.matchEnd', 'Fin'),
  13: () => t('events.halfTimeScore', 'Score mi-temps'),
  15: () => t('events.cardConfirmed', 'Carton confirmé'),
  16: () => t('events.penaltyMissed', 'Penalty manqué'),
  17: () => t('events.ownGoal', 'But contre son camp'),
  19: () => t('addedTime.label', 'Temps additionnel'),
  21: () => t('events.shotOnTarget', 'Tirs cadrés'),
  22: () => t('events.shotOffTarget', 'Tirs non cadrés'),
  23: () => t('events.attack', 'Attaques'),
  24: () => t('events.dangerousAttack', 'Attaques dangereuses'),
  25: () => t('events.possession', 'Possession'),
  26: () => t('events.extraTimeEnded', 'Prolongation terminée'),
  27: () => t('events.penaltyEnded', 'Penalty terminé'),
  28: () => t('events.var', 'VAR'),
  29: () => t('events.penaltyShootout', 'Penalty (TAB)'),
  30: () => t('events.penaltyShootoutMissed', 'Penalty manqué (TAB)'),
  37: () => t('events.blockedShot', 'Tirs bloqués')
}

/**
 * Obtenir le nom traduit d'un événement
 * @param {number} id - ID de l'événement
 * @returns {string} Nom traduit
 */
export function getStatName(id) {
  const statFunc = STAT_MAP[id]
  if (typeof statFunc === 'function') {
    return statFunc()
  }
  return `ID ${id}`
}

/**
 * Table de correspondance nom français → clé i18n
 * Pour traduire automatiquement les noms d'événements
 */
const EVENT_NAME_TO_I18N = {
  Possession: 'events.possession',
  Attaque: 'events.attack',
  'Attaque dangereuse': 'events.dangerousAttack',
  'Coup franc': 'events.freeKick',
  Touche: 'events.throwin',
  Corner: 'events.corner',
  '6 mètres': 'events.sixMeters',
  'Hors-jeu': 'events.offside',
  'Frappe cadrée': 'events.shotOnTarget',
  'Frappe non cadrée': 'events.shotOffTarget',
  Remplacement: 'events.substitution',
  'Joueur blessé': 'events.injury',
  'Carton jaune': 'events.yellowCard',
  'Carton rouge': 'events.redCard',
  Penalty: 'events.penalty',
  'Penalty (à confirmer)': 'events.penaltyUnconfirmed',
  But: 'events.goal',
  'Notification de but': 'events.goalNotification',
  "Coup d'envoi": 'events.kickoff',
  "Coup d'envoi 2ème mi-temps": 'events.secondHalfKickoff',
  'Ballon perdu (à confirmer)': 'events.ballLost',
  'Ballon récupéré (à confirmer)': 'events.ballRecovered',
  'Perte de balle (à confirmer)': 'events.ballLost',
  'Ballons perdus': 'events.ballLost',
  VAR: 'events.var',
  'Mi-temps': 'matchStates.halfTime',
  'Fin du match': 'matchStates.finished',
  'Temps additionnel': 'addedTime.label',
  'Tirs cadrés': 'events.shotOnTarget',
  'Tirs hors cadre': 'events.shotOffTarget',
  'Tirs bloqués': 'events.blockedShot',
  'Coup de pied de but': 'events.goalKick',
  'Coup franc dangereux': 'events.dangerousFreeKick',
  'Tirs aux buts marqués': 'events.penaltyShootoutScored',
  'Tirs aux buts manqués': 'events.penaltyShootoutMissed',
  'Tirs aux buts pour une équipe': 'events.penaltyShootoutForTeam',
  'VAR (Carton rouge)': 'events.varRedCard',
  "VAR (vérification d'un but)": 'events.varGoalCheck',
  'VAR (vérification penalty)': 'events.varPenaltyCheck',
  "Coup d'envoi prolongation": 'events.extraTimeKickoff',
  'Mi-temps prolongation': 'events.extraTimeHalfTime',
  "Coup d'envoi 2ème MT prolongation": 'events.extraTimeSecondHalfKickoff',
  'Fin de prolongation': 'events.extraTimeEnded',
  'Pause avant tirs aux buts': 'events.preShootoutPause'
}

/**
 * Traduire un nom d'événement
 * @param {string} eventName - Nom de l'événement en français
 * @returns {string} Nom traduit
 */
export function translateEventName(eventName) {
  const i18nKey = EVENT_NAME_TO_I18N[eventName]
  if (i18nKey) {
    return t(i18nKey, eventName)
  }
  return eventName // Retourner le nom original si pas de traduction
}

/**
 * Obtenir un événement non-officiel avec traduction
 * @param {number} id - ID de l'événement (buffer[26])
 * @param {number} byte27 - Byte 27 (buffer[27]) - 0x08 = HOME, 0x10 = AWAY, autre = neutre
 * @returns {object|null} { event: string, team: string|null, originalEvent: string }
 */
export function getNonOfficialEvent(id, byte27 = null) {
  const mapping = NON_OFFICIAL_EVENT_MAP[id]

  if (!mapping) {
    return null
  }

  // Si c'est un événement général (pas de byte 27)
  if (mapping.event && mapping.team !== undefined) {
    return {
      event: translateEventName(mapping.event),
      team: mapping.team,
      originalEvent: mapping.event
    }
  }

  // Si c'est un événement avec byte 27 (0x08 ou 0x10)
  if (byte27 !== null) {
    if (byte27 in mapping) {
      const eventData = mapping[byte27]
      if (eventData) {
        return {
          event: translateEventName(eventData.event),
          team: eventData.team,
          originalEvent: eventData.event
        }
      }
      return null
    }

    // Pas de fallback : le couple (ID, byte27) doit être exact
  }

  return null
}

/**
 * Table de correspondance des IDs non-officiels (F5 - offset 25-27)
 * Format: ID → { 0x08: {...}, 0x09: {...}, 0x10: {...} } ou { event, team } pour les événements neutres
 *
 * ⚡ NOUVEAU SYSTÈME : L'ID + Byte 27 déterminent l'équipe ET l'événement !
 *
 * Structure du message F5 :
 * - buffer[25] = 0x08 (marqueur fixe)
 * - buffer[26] = ID de l'événement
 * - buffer[27] = Byte 27 (équipe/flags)
 *   - 0x08 = HOME (IDs 191-255)
 *   - 0x09 = HOME continuation (quand dépasse 256, IDs redémarrent)
 *   - 0x10 = AWAY
 *   - autre = événement neutre
 *
 * 🔥 DÉCOUVERTE : Le système ne peut pas dépasser 256. Quand on atteint 256,
 * on passe de 0x08 à 0x09 et le compteur repart (ex: Coup franc HOME = 132 avec 0x09)
 *
 * Source : RadarID.md (IDs documentés officiellement)
 */
export const NON_OFFICIAL_EVENT_MAP = {
  // ════════════════════════════════════════════════════
  // TIRS AUX BUTS (Penalty Shootout)
  // ════════════════════════════════════════════════════

  181: { 0x10: { event: 'Tirs aux buts marqués', team: 'AWAY' } },
  182: { 0x10: { event: 'Tirs aux buts manqués', team: 'AWAY' } },
  183: { 0x10: { event: 'Tirs aux buts pour une équipe', team: 'AWAY' } },

  205: { 0x08: { event: 'Tirs aux buts marqués', team: 'HOME' } },
  206: {
    0x08: { event: 'Tirs aux buts manqués', team: 'HOME' },
    0x10: { event: "Coup d'envoi 2ème mi-temps", team: 'AWAY' }
  },
  207: { 0x08: { event: 'Tirs aux buts pour une équipe', team: 'HOME' } },

  // ════════════════════════════════════════════════════
  // PHASES DE JEU
  // ════════════════════════════════════════════════════

  191: { 0x10: { event: 'Attaque dangereuse', team: 'AWAY' } },
  192: { 0x10: { event: 'Attaque', team: 'AWAY' } },
  193: { 0x10: { event: 'Possession', team: 'AWAY' } },

  215: {
    0x08: { event: 'Attaque dangereuse', team: 'HOME' },
    0x10: { event: 'Joueur blessé', team: 'AWAY' }
  },
  216: { 0x08: { event: 'Attaque', team: 'HOME' } },
  217: {
    0x08: { event: 'Possession', team: 'HOME' },
    0x10: { event: 'Hors-jeu', team: 'AWAY' }
  },

  // ════════════════════════════════════════════════════
  // BUTS, CORNERS, CARTONS
  // ════════════════════════════════════════════════════

  194: { 0x10: { event: 'But', team: 'AWAY' } },
  218: {
    0x08: { event: 'But', team: 'HOME' },
    0x10: { event: 'Coup franc dangereux', team: 'AWAY' }
  },

  195: { 0x10: { event: 'Corner', team: 'AWAY' } },
  219: { 0x08: { event: 'Corner', team: 'HOME' } },

  196: { 0x10: { event: 'Carton jaune', team: 'AWAY' } },
  220: { 0x08: { event: 'Carton jaune', team: 'HOME' } },

  197: { 0x10: { event: 'Carton rouge', team: 'AWAY' } },
  221: {
    0x08: { event: 'Carton rouge', team: 'HOME' },
    0x10: { event: 'Ballons perdus', team: 'AWAY' }
  },

  // ════════════════════════════════════════════════════
  // 6 MÈTRES, PENALTIES
  // ════════════════════════════════════════════════════

  198: { 0x10: { event: '6 mètres', team: 'AWAY' } },
  222: {
    0x08: { event: '6 mètres', team: 'HOME' },
    0x10: { event: 'Ballons perdus', team: 'AWAY' }
  },

  199: { 0x10: { event: 'Penalty', team: 'AWAY' } },
  223: {
    0x08: { event: 'Penalty', team: 'HOME' },
    0x10: { event: 'Ballons perdus', team: 'AWAY' }
  },

  // ════════════════════════════════════════════════════
  // FRAPPES
  // ════════════════════════════════════════════════════

  201: { 0x10: { event: 'Frappe cadrée', team: 'AWAY' } },
  225: { 0x08: { event: 'Frappe cadrée', team: 'HOME' } },

  202: { 0x10: { event: 'Frappe non cadrée', team: 'AWAY' } },
  226: {
    0x08: { event: 'Frappe non cadrée', team: 'HOME' },
    0x10: { event: 'VAR (Carton rouge)', team: 'AWAY' }
  },

  // ════════════════════════════════════════════════════
  // REMPLACEMENTS
  // ════════════════════════════════════════════════════

  203: { 0x10: { event: 'Remplacement', team: 'AWAY' } },
  227: {
    0x08: { event: 'Remplacement', team: 'HOME' },
    0x10: { event: "VAR (vérification d'un but)", team: 'AWAY' }
  },

  // ════════════════════════════════════════════════════
  // COUPS D'ENVOI
  // ════════════════════════════════════════════════════

  204: { 0x10: { event: "Coup d'envoi", team: 'AWAY' } },
  228: {
    0x08: { event: "Coup d'envoi", team: 'HOME' },
    0x10: { event: 'VAR (vérification penalty)', team: 'AWAY' }
  },

  230: { 0x08: { event: "Coup d'envoi 2ème mi-temps", team: 'HOME' } },
  // 206/0x10 = Coup d'envoi 2ème mi-temps AWAY (voir section Tirs aux buts)

  // ════════════════════════════════════════════════════
  // TOUCHES, JOUEURS BLESSÉS, HORS-JEU
  // ════════════════════════════════════════════════════

  214: { 0x10: { event: 'Touche', team: 'AWAY' } },
  238: { 0x08: { event: 'Touche', team: 'HOME' } },

  239: { 0x08: { event: 'Joueur blessé', team: 'HOME' } },
  // 215/0x10 = Joueur blessé AWAY (voir section Phases de jeu)

  241: { 0x08: { event: 'Hors-jeu', team: 'HOME' } },
  // 217/0x10 = Hors-jeu AWAY (voir section Phases de jeu)

  // ════════════════════════════════════════════════════
  // COUPS FRANCS (0x08/0x10 + overflow 0x09)
  // ════════════════════════════════════════════════════

  242: { 0x08: { event: 'Coup franc dangereux', team: 'HOME' } },
  // 218/0x10 = Coup franc dangereux AWAY (voir section Buts)

  131: {
    0x09: { event: 'Coup franc dangereux', team: 'HOME' },
    0x01: { event: 'Fin de prolongation', team: null }
  },
  235: {
    0x10: { event: 'Coup franc dangereux', team: 'AWAY' },
    0x08: { event: 'Fin de prolongation', team: 'HOME' }
  },

  132: {
    0x09: { event: 'Coup franc', team: 'HOME' },
    0x01: { event: 'Pause avant tirs aux buts', team: null }
  },
  236: {
    0x10: { event: 'Coup franc', team: 'AWAY' },
    0x08: { event: 'Pause avant tirs aux buts', team: 'HOME' }
  },

  // ════════════════════════════════════════════════════
  // BALLONS PERDUS
  // ════════════════════════════════════════════════════

  245: { 0x08: { event: 'Ballons perdus', team: 'HOME' } },
  246: { 0x08: { event: 'Ballons perdus', team: 'HOME' } },
  247: { 0x08: { event: 'Ballons perdus', team: 'HOME' } },
  // 221/0x10, 222/0x10, 223/0x10 = Ballons perdus AWAY (voir sections ci-dessus)

  // ════════════════════════════════════════════════════
  // VAR
  // ════════════════════════════════════════════════════

  250: { 0x08: { event: 'VAR (Carton rouge)', team: 'HOME' } },
  // 226/0x10 = VAR (Carton rouge) AWAY (voir section Frappes)

  251: { 0x08: { event: "VAR (vérification d'un but)", team: 'HOME' } },
  // 227/0x10 = VAR (vérification d'un but) AWAY (voir section Remplacements)

  252: { 0x08: { event: 'VAR (vérification penalty)', team: 'HOME' } },
  // 228/0x10 = VAR (vérification penalty) AWAY (voir section Coups d'envoi)

  // ════════════════════════════════════════════════════
  // ÉVÉNEMENTS NEUTRES (sans équipe)
  // ════════════════════════════════════════════════════

  125: { event: 'Mi-temps', team: null },
  127: { event: 'Fin du match', team: null },
  136: { event: 'Temps additionnel', team: null },
  145: { event: 'VAR', team: null },

  // ════════════════════════════════════════════════════
  // PENALTY MANQUÉ (confirmé)
  // ════════════════════════════════════════════════════

  237: { 0x08: { event: 'Penalty manqué', team: 'HOME' } },
  213: { 0x10: { event: 'Penalty manqué', team: 'AWAY' } },

  // ════════════════════════════════════════════════════
  // PROLONGATION (Extra Time)
  // Confirmé par analyse des matchs :
  // - AS Roma vs Bologna (3-3, Europa League)
  // - Midtjylland vs Nottingham Forest (Europa League)
  // - AEK Larnaca vs Crystal Palace (Conference League)
  // Offsets : neutre +104 = HOME, neutre +80 = AWAY
  // ════════════════════════════════════════════════════

  // Coup d'envoi prolongation (neutre: 128, HOME: 232, AWAY: 208)
  128: { event: "Coup d'envoi prolongation", team: null },
  232: { 0x08: { event: "Coup d'envoi prolongation", team: 'HOME' } },
  208: { 0x10: { event: "Coup d'envoi prolongation", team: 'AWAY' } },

  // Mi-temps prolongation (neutre: 129, HOME: 233, AWAY: 209)
  129: { event: 'Mi-temps prolongation', team: null },
  233: { 0x08: { event: 'Mi-temps prolongation', team: 'HOME' } },
  209: { 0x10: { event: 'Mi-temps prolongation', team: 'AWAY' } },

  // Coup d'envoi 2ème MT prolongation (neutre: 130, HOME: 234, AWAY: 210)
  130: { event: "Coup d'envoi 2ème MT prolongation", team: null },
  234: { 0x08: { event: "Coup d'envoi 2ème MT prolongation", team: 'HOME' } },
  210: { 0x10: { event: "Coup d'envoi 2ème MT prolongation", team: 'AWAY' } },

  // Fin de prolongation (neutre: 131/0x01, HOME: 235/0x08, AWAY: 211)
  // 131 et 235 ont aussi des mappings Coup franc (0x09 et 0x10) — voir section Coups francs
  211: { 0x10: { event: 'Fin de prolongation', team: 'AWAY' } },

  // Pause avant tirs aux buts (neutre: 132/0x01, HOME: 236/0x08, AWAY: 212)
  // 132 et 236 ont aussi des mappings Coup franc (0x09 et 0x10) — voir section Coups francs
  212: { 0x10: { event: 'Pause avant tirs aux buts', team: 'AWAY' } }
}

/**
 * IDs non-officiels F5 à ignorer complètement (aucun effet sur le radar).
 * 175 = message post-but (kick-off) qui écrasait la card de but.
 */
export const IGNORED_EVENT_IDS = [143, 151, 175]

/**
 * Événements mappés mais exclus du radar (pas de mouvement de balle).
 * Ces événements restent visibles dans les annotations F5.
 */
export const RADAR_EXCLUDED_EVENTS = []

/**
 * Liste des IDs d'événements valides (pour recherche dans les buffers)
 */
export const VALID_EVENT_IDS = [
  1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 15, 16, 17, 19, 21, 22, 23, 24, 26,
  27, 28, 29, 30, 37
]

/**
 * Icônes pour chaque type d'événement
 */
export const EVENT_ICONS = {
  Possession: '📊',
  Attaque: '⚡',
  'Attaque dangereuse': '🔥',
  'Coup franc': '⚽',
  Touche: '🤾',
  Corner: '🚩',
  '6 mètres': '🥅',
  'Hors-jeu': '🚫',
  'Frappe cadrée': '🎯',
  Frappe: '💥',
  Remplacement: '🔄',
  'Joueur blessé': '🏥',
  'Carton jaune': '🟨',
  'Carton rouge': '🟥',
  Penalty: '⚖️',
  'Penalty (à confirmer)': '⚖️',
  But: '⚽',
  'Notification de but': '🔔',
  "Coup d'envoi": '⚪',
  "Coup d'envoi 2ème mi-temps": '⚪',
  'Ballon perdu (à confirmer)': '🔴',
  'Ballon récupéré (à confirmer)': '🟢',
  'Perte de balle (à confirmer)': '🔴',
  'Ballons perdus': '🔴',
  '? (identifié mais inconnu)': '❓',
  VAR: '📺',
  'Mi-temps': '⏸️',
  'Temps additionnel': '⏱️',
  'Tirs cadrés': '🎯',
  'Tirs hors cadre': '💨',
  'Tirs bloqués': '🛡️',
  'Coup de pied de but': '🥅',
  'Coup franc dangereux': '⚽',
  'Tirs aux buts marqués': '⚽',
  'Tirs aux buts manqués': '❌',
  'Tirs aux buts pour une équipe': '⚽',
  'VAR (Carton rouge)': '📺',
  "VAR (vérification d'un but)": '📺',
  'VAR (vérification penalty)': '📺',
  "Coup d'envoi prolongation": '⏱️',
  'Mi-temps prolongation': '⏸️',
  "Coup d'envoi 2ème MT prolongation": '⏱️',
  'Fin de prolongation': '🏁',
  'Pause avant tirs aux buts': '⚽'
}

/**
 * Stats importantes à afficher en priorité
 */
export const IMPORTANT_STATS = [
  'But',
  'Tirs cadrés',
  'Tirs hors cadre',
  'Corner',
  'Possession',
  'Attaques',
  'Attaques dangereuses',
  'Carton jaune',
  'Carton rouge',
  'Tirs bloqués'
]

/**
 * Options d'annotation pour les messages F5
 */
export const ANNOTATION_OPTIONS = [
  { value: '', label: '-- Sélectionner --' },
  { value: 'Possession', label: '📊 Possession' },
  { value: 'Attaque', label: '⚡ Attaque' },
  { value: 'Attaque dangereuse', label: '🔥 Attaque dangereuse' },
  { value: 'Touche', label: '🤾 Touche' },
  { value: '6 mètres', label: '🥅 6 mètres' },
  { value: 'Coup franc', label: '⚽ Coup franc' },
  { value: 'Frappe cadrée', label: '🎯 Frappe cadrée' },
  { value: 'Tirs hors cadre', label: '💨 Tirs hors cadre' },
  { value: 'Frappe', label: '💥 Frappe' },
  { value: 'Corner', label: '🚩 Corner' },
  { value: 'Remplacement', label: '🔄 Remplacement' },
  { value: 'Joueur blessé', label: '🏥 Joueur blessé' },
  { value: 'Carton jaune', label: '🟨 Carton jaune' },
  { value: 'Carton rouge', label: '🟥 Carton rouge' },
  { value: 'Hors-jeu', label: '🚫 Hors-jeu' },
  { value: 'Penalty', label: '⚖️ Penalty' },
  { value: 'Penalty (à confirmer)', label: '⚖️ Penalty (à confirmer)' },
  { value: 'But', label: '⚽ But' },
  { value: 'Notification de but', label: '🔔 Notification de but' },
  { value: "Coup d'envoi", label: "⚪ Coup d'envoi" },
  {
    value: "Coup d'envoi 2ème mi-temps",
    label: "⚪ Coup d'envoi 2ème mi-temps"
  },
  {
    value: 'Ballon perdu (à confirmer)',
    label: '🔴 Ballon perdu (à confirmer)'
  },
  {
    value: 'Ballon récupéré (à confirmer)',
    label: '🟢 Ballon récupéré (à confirmer)'
  },
  {
    value: 'Perte de balle (à confirmer)',
    label: '🔴 Perte de balle (à confirmer)'
  },
  { value: 'Ballons perdus', label: '🔴 Ballons perdus' },
  {
    value: '? (identifié mais inconnu)',
    label: '❓ ? (identifié mais inconnu)'
  },
  { value: 'VAR', label: '📺 VAR' },
  { value: "VAR (vérification d'un but)", label: "📺 VAR (vérification d'un but)" },
  { value: 'VAR (Carton rouge)', label: '📺 VAR (Carton rouge)' },
  { value: 'VAR (vérification penalty)', label: '📺 VAR (vérification penalty)' },
  { value: 'Coup franc dangereux', label: '⚽ Coup franc dangereux' },
  { value: 'Tirs aux buts marqués', label: '⚽ Tirs aux buts marqués' },
  { value: 'Tirs aux buts manqués', label: '❌ Tirs aux buts manqués' },
  { value: 'Fin du match', label: '🏁 Fin du match' },
  { value: 'Mi-temps', label: '⏸️ Mi-temps' },
  { value: 'Temps additionnel', label: '⏱️ Temps additionnel' },
  { value: "Coup d'envoi prolongation", label: "⏱️ Coup d'envoi prolongation" },
  { value: 'Mi-temps prolongation', label: '⏸️ Mi-temps prolongation' },
  {
    value: "Coup d'envoi 2ème MT prolongation",
    label: "⏱️ Coup d'envoi 2ème MT prolongation"
  },
  { value: 'Fin de prolongation', label: '🏁 Fin de prolongation' },
  { value: 'Pause avant tirs aux buts', label: '⚽ Pause avant tirs aux buts' },
  { value: 'Autre', label: '❓ Autre' }
]

/**
 * 🎯 Système de priorité des événements
 *
 * Règle : Un événement avec délai ne peut être interrompu que par un événement de priorité SUPÉRIEURE.
 *
 * Priorités:
 * - 2 = MAJEUR (But, Carton rouge, VAR, Penalty) - délai 4-5s
 * - 1 = MOYEN (Frappe, Corner, Coup franc, etc.) - délai 2-3s
 * - 0 = FLUX (Possession, Attaque, Touche) - pas de délai
 */
export const EVENT_PRIORITY = {
  // 🔴 MAJEUR (priorité 2)
  But: {
    priority: 2,
    minDelay: 10000,
    interruptibleBy: [
      'VAR',
      "VAR (vérification d'un but)",
      'VAR but annulé',
      'VAR (Carton rouge)',
      'VAR (vérification penalty)',
      'Hors-jeu',
    ],
  },
  'Carton rouge': { priority: 2, minDelay: 4000 },
  VAR: { priority: 2, minDelay: 4000 },
  "VAR (vérification d'un but)": { priority: 2, minDelay: 4000 },
  'VAR but annulé': { priority: 2, minDelay: 4000 },
  Penalty: { priority: 2, minDelay: 4000 },
  'Penalty manqué': { priority: 2, minDelay: 4000 },
  'VAR (Carton rouge)': { priority: 2, minDelay: 4000 },
  'VAR (vérification penalty)': { priority: 2, minDelay: 4000 },
  'Tirs aux buts marqués': { priority: 2, minDelay: 4000 },
  'Tirs aux buts manqués': { priority: 2, minDelay: 4000 },

  // 🟠 MOYEN (priorité 1) - délai 2-3 secondes
  'Carton jaune': { priority: 1, minDelay: 3000 },
  'Frappe cadrée': { priority: 1, minDelay: 1000 },
  'Frappe non cadrée': { priority: 1, minDelay: 1000 },
  'Tir non cadré': { priority: 1, minDelay: 1000 },
  Corner: { priority: 1, minDelay: 1000 },
  'Coup franc': { priority: 1, minDelay: 1000 },
  'Coup franc dangereux': { priority: 1, minDelay: 1000 },
  Remplacement: { priority: 1, minDelay: 3000 },
  'Joueur blessé': { priority: 1, minDelay: 3000 },
  'Hors-jeu': { priority: 1, minDelay: 1000 },
  "Coup d'envoi": { priority: 1, minDelay: 2500 },
  "Coup d'envoi 2ème mi-temps": { priority: 1, minDelay: 2500 },
  '6 mètres': { priority: 1, minDelay: 1000 },

  // 🟢 PERTE DE BALLE (priorité 0) - flux comme la possession
  'Ballons perdus': { priority: 0, minDelay: 500 },

  // 🟢 FLUX (priorité 0) - délai minimum pour éviter le clignotement
  Possession: { priority: 0, minDelay: 500 },
  Attaque: { priority: 0, minDelay: 500 },
  'Attaque dangereuse': { priority: 0, minDelay: 500 },
  Touche: { priority: 1, minDelay: 1000 },

  // États du match (priorité spéciale - restent affichés)
  'Mi-temps': { priority: 2, minDelay: 10000 },
  'Fin du match': { priority: 2, minDelay: 10000 },
  'Début du match': { priority: 2, minDelay: 5000 },
  'Temps additionnel': { priority: 1, minDelay: 3000 },

  // Prolongation & tirs aux buts
  "Coup d'envoi prolongation": { priority: 2, minDelay: 5000 },
  'Mi-temps prolongation': { priority: 2, minDelay: 10000 },
  "Coup d'envoi 2ème MT prolongation": { priority: 2, minDelay: 5000 },
  'Fin de prolongation': { priority: 2, minDelay: 10000 },
  'Pause avant tirs aux buts': { priority: 2, minDelay: 10000 }
}

/**
 * Obtenir la priorité et le délai minimum d'un événement
 * @param {string} eventType - Type d'événement
 * @returns {{ priority: number, minDelay: number }} - Priorité (0-2) et délai en ms
 */
export function getEventPriority(eventType) {
  return EVENT_PRIORITY[eventType] || { priority: 0, minDelay: 0 }
}

/**
 * Vérifier si un événement peut interrompre l'événement actuel
 * @param {string} newEvent - Nouvel événement
 * @param {string} currentEvent - Événement actuel
 * @param {number} currentEndTime - Timestamp de fin minimum de l'événement actuel
 * @returns {boolean} - true si le nouvel événement peut s'afficher
 */
export function canInterruptEvent(newEvent, currentEvent, currentEndTime) {
  const now = Date.now()

  // Si pas d'événement actuel ou délai passé, on peut afficher
  if (!currentEvent || now >= currentEndTime) {
    return true
  }

  const currentConfig = getEventPriority(currentEvent)

  // Si l'événement actuel a une whitelist, seuls ces événements peuvent l'interrompre
  if (currentConfig.interruptibleBy) {
    return currentConfig.interruptibleBy.includes(newEvent)
  }

  const newPriority = getEventPriority(newEvent).priority

  // Seule une priorité SUPÉRIEURE peut interrompre
  return newPriority > currentConfig.priority
}

/**
 * Obtenir le nom d'une stat par son ID
 */
export function getStatNameById(id) {
  const statFunc = STAT_MAP[id]
  if (typeof statFunc === 'function') {
    return statFunc()
  }
  return null
}

/**
 * Obtenir l'icône d'un événement
 */
export function getEventIcon(eventName) {
  return EVENT_ICONS[eventName] || '📊'
}

/**
 * Obtenir les infos d'un ID non-officiel
 * @param {number} id - ID de l'événement (buffer[26])
 * @param {number} byte27 - Byte 27 (buffer[27]) - 0x08 = HOME, 0x10 = AWAY
 * @returns {object|null} { event: string, team: string|null }
 */
export function getNonOfficialEventInfo(id, byte27 = null) {
  const mapping = NON_OFFICIAL_EVENT_MAP[id]

  if (!mapping) {
    return null
  }

  // Si c'est un événement général (pas de byte 27)
  if (mapping.event && mapping.team !== undefined) {
    return mapping
  }

  // Si c'est un événement avec byte 27
  if (byte27 !== null && mapping[byte27]) {
    return mapping[byte27]
  }

  // Pas de fallback : le couple (ID, byte27) doit être exact
  return null
}
